// Sections: named containers that group a run of entries inside a form, and may
// hold other sections.
//
// A form's `schema.fields` is the ordered list of everything on the page. Most
// entries are questions, exactly as before sections existed; a section is an
// entry of its own that carries what it holds, questions and subsections alike:
//
//   fields: [
//     { id: 'field_1', type: 'text', ... },
//     { id: 'section_1', type: 'section', name: 'Exterior', fields: [
//       { id: 'field_4', type: 'text', ... },
//       { id: 'section_2', type: 'section', name: 'Roof', fields: [ ...questions ] },
//     ] },
//     { id: 'field_9', type: 'number', ... },
//   ]
//
// Keeping sections inside `fields` rather than in a list of their own means a
// form saved before sections existed is already a valid tree — nothing to
// migrate — and a section's position and its contents can never disagree. It
// also means nesting costs nothing structurally: a section's `fields` is the
// same kind of list as the form's own, so every operation here is written once
// and recurses.
//
// Everything is a pure function over that tree. The builder's drag, keyboard
// and delete operations are all expressed as one of them, so the awkward cases
// (dropping a question out of the bottom of a section, dragging a section into
// a section it already contains) are decided in one place and tested there.

export const SECTION_TYPE = 'section';

// What an unnamed section is called wherever it has to be printed.
export const UNTITLED_SECTION = 'Untitled section';

export const isSection = (entry) => entry?.type === SECTION_TYPE;

export const sectionFields = (entry) => (Array.isArray(entry?.fields) ? entry.fields : []);

export const sectionTitle = (entry) => (entry?.name || '').trim() || UNTITLED_SECTION;

let sectionSequence = 0;

// Same id scheme as a question: unique within a form, and proof against two
// sections created in the same millisecond.
export const newSection = (name = '') => {
  sectionSequence += 1;
  return {
    id: `section_${Date.now()}_${sectionSequence}`,
    type: SECTION_TYPE,
    name,
    fields: [],
  };
};

// Every question on the form, in the order it is answered — sections dissolved,
// however deeply they are stacked. This is what report rendering, Excel export
// and calculations work from: they care about the questions, not the grouping.
export const flattenFields = (entries = []) =>
  (Array.isArray(entries) ? entries : []).reduce(
    (fields, entry) => (isSection(entry)
      ? fields.concat(flattenFields(sectionFields(entry)))
      : fields.concat([entry])),
    [],
  );

// Every section on the form, outer before inner. Used to say how much a section
// holds without walking the tree at the call site.
export const flattenSections = (entries = []) =>
  (Array.isArray(entries) ? entries : []).reduce(
    (sections, entry) => (isSection(entry)
      ? sections.concat([entry], flattenSections(sectionFields(entry)))
      : sections),
    [],
  );

export const getSchemaEntries = (schema) => (Array.isArray(schema?.fields) ? schema.fields : []);

export const getSchemaFields = (schema) => flattenFields(getSchemaEntries(schema));

// Coerce stored entries into the shape above: a section always has a name and a
// list of entries, at every depth.
export const normalizeEntries = (rawEntries) =>
  (Array.isArray(rawEntries) ? rawEntries : []).map((entry) => (isSection(entry)
    ? {
      ...entry,
      type: SECTION_TYPE,
      name: typeof entry.name === 'string' ? entry.name : '',
      fields: normalizeEntries(sectionFields(entry)),
    }
    : entry));

// --- Locating entries ------------------------------------------------------

// Where an entry sits: `containerId` is null at the top level, or the id of the
// section directly holding it — however deep that section itself is.
export const locateEntry = (entries, id, containerId = null) => {
  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i];
    if (entry.id === id) return { containerId, index: i };
    if (isSection(entry)) {
      const nested = locateEntry(sectionFields(entry), id, entry.id);
      if (nested) return nested;
    }
  }
  return null;
};

export const findEntry = (entries, id) => {
  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i];
    if (entry.id === id) return entry;
    if (isSection(entry)) {
      const nested = findEntry(sectionFields(entry), id);
      if (nested) return nested;
    }
  }
  return null;
};

// The sections enclosing an entry, outermost first. How deep a card is drawn,
// and which headings are above it, both read off this.
export const ancestorIds = (entries, id, trail = []) => {
  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i];
    if (entry.id === id) return trail;
    if (isSection(entry)) {
      const nested = ancestorIds(sectionFields(entry), id, trail.concat([entry.id]));
      if (nested) return nested;
    }
  }
  return null;
};

// Whether `id` is somewhere inside `entry` — the question a section must answer
// before it can be dropped anywhere, since it cannot land inside itself.
export const contains = (entry, id) =>
  isSection(entry) && sectionFields(entry).some((child) => child.id === id || contains(child, id));

// Ids top to bottom as they appear on screen, a section before what it holds.
// Used to tell which way a drag is travelling.
export const documentOrder = (entries) =>
  (Array.isArray(entries) ? entries : []).reduce(
    (ids, entry) => (isSection(entry)
      ? ids.concat([entry.id], documentOrder(sectionFields(entry)))
      : ids.concat([entry.id])),
    [],
  );

// --- Splicing --------------------------------------------------------------

// Apply `transform` to the entry list of one container: the form itself when
// `containerId` is null, otherwise the section with that id wherever it sits.
// Sections off the path are handed back untouched, so an edit in one corner of
// the tree can't churn the rest of it.
const mapContainer = (entries, containerId, transform) => {
  if (containerId === null) return transform(entries);
  let changed = false;
  const next = entries.map((entry) => {
    if (!isSection(entry)) return entry;
    const fields = sectionFields(entry);
    const updated = entry.id === containerId
      ? transform(fields)
      : mapContainer(fields, containerId, transform);
    if (updated === fields) return entry;
    changed = true;
    return { ...entry, fields: updated };
  });
  return changed ? next : entries;
};

// Apply `updater` to one entry wherever it lives, by id.
export const mapEntryById = (entries, id, updater) => {
  let changed = false;
  const next = entries.map((entry) => {
    if (entry.id === id) {
      changed = true;
      return updater(entry);
    }
    if (!isSection(entry)) return entry;
    const fields = sectionFields(entry);
    const updated = mapEntryById(fields, id, updater);
    if (updated === fields) return entry;
    changed = true;
    return { ...entry, fields: updated };
  });
  return changed ? next : entries;
};

const withoutAt = (entries, location) => mapContainer(
  entries,
  location.containerId,
  (list) => list.filter((_, index) => index !== location.index),
);

const insertAt = (entries, containerId, index, item) => mapContainer(entries, containerId, (list) => {
  const next = [...list];
  next.splice(index, 0, item);
  return next;
});

// --- Moving ----------------------------------------------------------------

// A drop position, expressed relative to something already on the page rather
// than as an index — indexes shift the moment the dragged entry is lifted out,
// and every off-by-one that causes is a card landing one place from where the
// pointer was.
export const before = (id) => ({ kind: 'before', id });
export const after = (id) => ({ kind: 'after', id });
export const sectionStart = (id) => ({ kind: 'sectionStart', id });
export const sectionEnd = (id) => ({ kind: 'sectionEnd', id });

// Where a drop position lands. `sectionStart`/`sectionEnd` are the two places
// no neighbouring card can name: first and last inside a section, which is how
// an entry is dropped into an empty one and how it is kept inside a section
// rather than after it.
const destinationOf = (entries, target) => {
  if (target.kind === 'sectionStart' || target.kind === 'sectionEnd') {
    const section = findEntry(entries, target.id);
    if (!isSection(section)) return null;
    return {
      containerId: section.id,
      index: target.kind === 'sectionStart' ? 0 : sectionFields(section).length,
    };
  }
  const location = locateEntry(entries, target.id);
  if (!location) return null;
  return {
    containerId: location.containerId,
    index: location.index + (target.kind === 'after' ? 1 : 0),
  };
};

// Move an entry to a drop position. A section travels with everything inside
// it and lands wherever a question could, bar one place: inside itself, which
// would take the destination out of the form along with the section. Returns
// `entries` itself when the move is a no-op — the entry is already there, or
// the target has gone — so a caller can use identity to tell whether anything
// actually happened.
export const moveEntry = (entries, dragId, target) => {
  if (!target || target.id === dragId) return entries;
  const from = locateEntry(entries, dragId);
  const dragged = findEntry(entries, dragId);
  if (!from || !dragged) return entries;
  if (contains(dragged, target.id)) return entries;

  // Resolved against the tree with the entry already lifted out, so the target
  // means the same thing whether the entry is travelling up or down.
  const rest = withoutAt(entries, from);
  const destination = destinationOf(rest, target);
  if (!destination) return entries;
  if (destination.containerId === from.containerId && destination.index === from.index) return entries;

  return insertAt(rest, destination.containerId, destination.index, dragged);
};

// Every gap an entry can be dropped into, top to bottom: before each card, then
// down through any section it meets, and one at the foot of every container.
// The gap after a section's last card and the gap after the section itself are
// two distinct slots at the same place on screen: that pair is what stepping
// out of a section with the keyboard passes through.
const slotsOf = (entries, containerId = null) => {
  const slots = [];
  entries.forEach((entry, index) => {
    slots.push({ containerId, index });
    if (isSection(entry)) slots.push(...slotsOf(sectionFields(entry), entry.id));
  });
  slots.push({ containerId, index: entries.length });
  return slots;
};

// Keyboard equivalent of a drag: one slot up or down, into and out of sections
// the same way the pointer travels through them. This is also the only way to
// reorder on a touch device, where HTML5 drag events never fire.
export const stepEntry = (entries, id, direction) => {
  const from = locateEntry(entries, id);
  const dragged = findEntry(entries, id);
  if (!from || !dragged) return entries;

  // Slots are counted with the entry lifted out, so a section's own insides are
  // not among the places it could step into.
  const rest = withoutAt(entries, from);
  const slots = slotsOf(rest);
  const current = slots.findIndex(
    (slot) => slot.containerId === from.containerId && slot.index === from.index,
  );
  const target = current === -1 ? null : slots[current + direction];
  if (!target) return entries;
  return insertAt(rest, target.containerId, target.index, dragged);
};

// --- Adding and removing ---------------------------------------------------

// Append at the very bottom of the form. When the form ends in a section that
// means inside it, and inside the last section of that one in turn: the button
// sits below everything on the page, so that is where the next question is
// expected to appear.
export const appendField = (entries, field) => {
  const last = entries[entries.length - 1];
  if (!isSection(last)) return [...entries, field];
  return entries
    .slice(0, -1)
    .concat([{ ...last, fields: appendField(sectionFields(last), field) }]);
};

// Append to one section — what the button at the foot of a section does, and
// the only way to put a question into an empty one without dragging.
export const appendFieldToSection = (entries, sectionId, field) => {
  const section = findEntry(entries, sectionId);
  if (!isSection(section)) return appendField(entries, field);
  return insertAt(entries, sectionId, sectionFields(section).length, field);
};

// Insert a question into the gap it was started in — in the form itself
// (`containerId` null) or in a section at any depth. Unlike a section it takes
// nothing with it: what follows the gap stays where it was, below the new
// question.
export const insertFieldAt = (entries, containerId, index, field) => {
  if (containerId !== null && !isSection(findEntry(entries, containerId))) {
    return appendField(entries, field);
  }
  return insertAt(entries, containerId, index, field);
};

// Insert a section into a container — the form itself, or a section already on
// the page — taking the questions that follow it with it, up to the next
// section, which starts a group of its own. A section is a chapter marker:
// dropping one in front of a run of questions is meant to name that run, not to
// leave an empty box the author then has to fill by dragging every question
// into it one at a time. Started inside a section, that is a subsection: the
// questions below the gap become a named part of the chapter they were already
// in, rather than leaving it.
export const insertSectionAt = (entries, containerId, index, section) =>
  mapContainer(entries, containerId, (list) => {
    const following = list.slice(index);
    let captured = 0;
    while (captured < following.length && !isSection(following[captured])) captured += 1;
    return [
      ...list.slice(0, index),
      { ...section, fields: sectionFields(section).concat(following.slice(0, captured)) },
      ...following.slice(captured),
    ];
  });

// Delete an entry wherever it sits — a question, or a section and everything
// inside it, subsections included.
export const removeEntryById = (entries, id) => {
  let changed = false;
  const next = entries.reduce((kept, entry) => {
    if (entry.id === id) {
      changed = true;
      return kept;
    }
    if (!isSection(entry)) return kept.concat([entry]);
    const fields = sectionFields(entry);
    const updated = removeEntryById(fields, id);
    if (updated === fields) return kept.concat([entry]);
    changed = true;
    return kept.concat([{ ...entry, fields: updated }]);
  }, []);
  return changed ? next : entries;
};

// Delete only the section, leaving what it held where it was — its questions
// and any subsections come out into the section around it, in the same place on
// the page.
export const removeSectionOnly = (entries, sectionId) =>
  entries.reduce((next, entry) => {
    if (entry.id === sectionId && isSection(entry)) return next.concat(sectionFields(entry));
    if (!isSection(entry)) return next.concat([entry]);
    return next.concat([{ ...entry, fields: removeSectionOnly(sectionFields(entry), sectionId) }]);
  }, []);

export const renameSection = (entries, sectionId, name) =>
  mapEntryById(entries, sectionId, (entry) => (isSection(entry) ? { ...entry, name } : entry));
